import PublicPage from "./PublicPage.jsx";
import {
  SUPPORT_EMAIL,
  LEGAL_ENTITY,
  LEGAL_JURISDICTION,
  LEGAL_COURTS,
} from "./legalInfo.js";

export default function TermsPage({ onNavigate, onGetStarted }) {
  return (
    <PublicPage
      title="Terms of Service"
      lastUpdated="August 2026"
      onNavigate={onNavigate}
      onGetStarted={onGetStarted}
    >
      <section className="legal-section">
        <h2 className="landing-h2 legal-h2">1. Acceptance of terms</h2>
        <p className="legal-p">
          By accessing or using Simple Expense Management ("the Service"),
          you agree to be bound by these Terms of Service. If you do not
          agree, do not use the Service. If you are a consumer in the
          EU/EEA, mandatory consumer protection laws of your country of
          residence apply and these terms do not limit rights you cannot
          legally waive.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="landing-h2 legal-h2">2. Description of service</h2>
        <p className="legal-p">
          Simple Expense Management lets you photograph or upload receipts
          and invoices; an AI model extracts the vendor, date, amount and
          category so you can save the expense and generate quarterly
          reports. An account is required; you purchase scan credits which
          are consumed as you scan receipts. Every new account gets one
          free scan with no purchase required. The Service is provided by{" "}
          {LEGAL_ENTITY}.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="landing-h2 legal-h2">3. Accounts</h2>
        <p className="legal-p">
          An account is required to use the Service, identified by email
          address — there is no password; you sign in with a one-time code
          sent to your email. You are responsible for keeping access to
          that email address secure, and for all activity that occurs under
          your account. An account owner may invite a read-only
          "shared access" account to view (but not edit or scan) their data.
          We reserve the right to suspend or terminate accounts that
          violate these terms, with notice where required by applicable
          law.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="landing-h2 legal-h2">4. Credits and payments</h2>
        <p className="legal-p">
          Scan credits are purchased in advance and deducted only after a
          receipt is successfully scanned — a failed scan is never charged.
          Credits have no cash value and are non-transferable. Unused
          purchased credits do not expire. Bigger credit packs cost less
          per scan than smaller ones; the price of each pack is shown
          before purchase. Payments are processed securely by Stripe.
          Refund terms are set out in our{" "}
          <button className="link-btn" onClick={() => onNavigate("refunds")}>
            Refund &amp; Cancellation Policy
          </button>
          .
        </p>
        <p className="legal-p">
          <strong>EU consumers — right of withdrawal:</strong> Under EU
          Consumer Rights Directive 2011/83/EU, you have a 14-day right of
          withdrawal from the date of purchase. The right of withdrawal
          expires in respect of any scan credits that have already been
          used (Art. 16(m) CRD). Unused credits remain refundable within
          the 14-day window. Full details are in our{" "}
          <button className="link-btn" onClick={() => onNavigate("refunds")}>
            Refund &amp; Cancellation Policy
          </button>
          .
        </p>
      </section>

      <section className="legal-section">
        <h2 className="landing-h2 legal-h2">5. Acceptable use</h2>
        <p className="legal-p">
          You agree to use the Service only to process your own genuine
          receipts and invoices, and not to upload content that is
          illegal, fraudulent, or violates the policies of our underlying
          AI or payment providers. We reserve the right to suspend access
          for any violation. For EU users, suspension will be accompanied
          by notification and a statement of reasons, except where doing so
          would be unlawful or compromise security.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="landing-h2 legal-h2">6. Third-party AI providers</h2>
        <p className="legal-p">
          Receipt data is extracted by a third-party AI model (currently
          Anthropic Claude, accessed via OpenRouter). We do not control its
          output and are not responsible for the accuracy of any
          AI-extracted field — always review extracted amounts before
          relying on them for accounting or tax purposes. See our{" "}
          <button className="link-btn" onClick={() => onNavigate("privacy")}>
            Privacy Policy
          </button>{" "}
          for details on how receipt images are handled.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="landing-h2 legal-h2">7. Intellectual property</h2>
        <p className="legal-p">
          You retain ownership of the receipts, invoices, and data you
          upload. The Simple Expense Management platform, branding, and
          software are owned by {LEGAL_ENTITY} and may not be copied or
          reproduced without permission.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="landing-h2 legal-h2">8. Limitation of liability</h2>
        <p className="legal-p">
          The Service is provided "as is" without warranties of any kind.
          AI-extracted data may contain errors — you are responsible for
          verifying figures used in your accounts or tax filings. We are
          not liable for any indirect, incidental, or consequential damages
          arising from your use of the Service. Our total liability to you
          for any claim is limited to the amount you paid us in the 30 days
          preceding the claim. Nothing in these terms limits liability for
          death or personal injury caused by negligence, fraud, or any
          liability that cannot be excluded under applicable law.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="landing-h2 legal-h2">9. Changes to terms</h2>
        <p className="legal-p">
          We may update these terms at any time. For non-material changes
          (such as clarifications), we will update the "last updated" date.
          For material changes that affect your rights, we will notify
          registered users by email at least 30 days before the change
          takes effect. Continued use of the Service after the effective
          date of a material change constitutes acceptance. If you do not
          accept material changes, you may stop using the Service and, if
          you have unused paid credits, request a refund in accordance with
          our Refund Policy.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="landing-h2 legal-h2">10. Governing law and disputes</h2>
        <p className="legal-p">
          These terms are governed by the law of {LEGAL_JURISDICTION}. Any
          dispute that cannot be resolved amicably will be submitted to the
          exclusive jurisdiction of the courts of {LEGAL_COURTS}, except
          that consumers in the EU/EEA always retain the right to bring
          proceedings before the courts of their country of residence and
          to benefit from the mandatory consumer protection provisions of
          their local law.
        </p>
        <p className="legal-p">
          EU consumers may also use the European Commission's Online
          Dispute Resolution platform at{" "}
          <a
            href="https://ec.europa.eu/consumers/odr"
            target="_blank"
            rel="noopener noreferrer"
          >
            ec.europa.eu/consumers/odr
          </a>
          .
        </p>
      </section>

      <section className="legal-section">
        <h2 className="landing-h2 legal-h2">11. Contact</h2>
        <p className="legal-p">
          Questions about these terms? Contact us at{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </p>
      </section>
    </PublicPage>
  );
}
